/**
 * Gelübde-Bibliothek — Seed-Daten
 *
 * Struktur: 147 Gelübde mit globalem Integer-ID (entspricht PDF-Nummerierung).
 * Sprachblöcke: de (Pflicht) + en (optional).
 */

export type VowCategory =
  | "refuge-commitments"
  | "ten-virtues"
  | "bodhisattva-root"
  | "bodhisattva-secondary"
  | "bodhisattva-helping"
  | "bodhisattva-enlightenment"
  | "bodhisattva-black-white"
  | "lay-vows";

export interface VowTranslation {
  title: string;
  description: string;
  positiveFormulation?: string;
  conditions?: string;
  exceptions?: string;
  karmicCorrelation?: string;
  reflectionQuestions?: string[];
  everydayExamples?: string[];
}

export interface Vow {
  id: number;
  category: VowCategory;
  titleSanskrit?: string;
  alwaysActive: boolean;
  de: VowTranslation;
  en?: VowTranslation;
}

export interface VowCategoryInfo {
  id: VowCategory;
  label: string;
  labelShort: string;
  description: string;
  count: number;
  alwaysActive: boolean;
}

export const VOW_CATEGORIES: VowCategoryInfo[] = [
  {
    id: "ten-virtues",
    label: "10 Tugenden",
    labelShort: "Tugenden",
    description: "Die zehn tugendhaften Handlungen — Pflicht bei jedem Check-in",
    count: 10,
    alwaysActive: true,
  },
  {
    id: "refuge-commitments",
    label: "12 Zufluchtsversprechen",
    labelShort: "Zuflucht",
    description: "Die 12 Versprechen der Zufluchtnahme",
    count: 12,
    alwaysActive: false,
  },
  {
    id: "bodhisattva-root",
    label: "Grundsatz-Bodhisattva-Gelübde",
    labelShort: "Grundsatz",
    description: "Die Wurzelgelübde des Bodhisattva (Nr. 23–57)",
    count: 35,
    alwaysActive: false,
  },
  {
    id: "bodhisattva-secondary",
    label: "Zweitrangige Bodhisattva-Gelübde",
    labelShort: "Zweitrangig",
    description: "Nebengelübde des Bodhisattva — Vollendung der 6 Paramitas (Nr. 58–106)",
    count: 49,
    alwaysActive: false,
  },
  {
    id: "bodhisattva-helping",
    label: "Helfen-Gelübde",
    labelShort: "Helfen",
    description: "Zweitrangige Verfehlungen gegen die Moral, anderen zu helfen (Nr. 107–125)",
    count: 19,
    alwaysActive: false,
  },
  {
    id: "bodhisattva-enlightenment",
    label: "Erleuchtungswunsch-Gelübde",
    labelShort: "Erleuchtung",
    description: "Zweitrangige Verfehlungen gegen den Wunsch nach Erleuchtung (Nr. 126–134)",
    count: 9,
    alwaysActive: false,
  },
  {
    id: "bodhisattva-black-white",
    label: "Schwarze und Weiße Taten",
    labelShort: "Schwarz/Weiß",
    description: "Die vier schwarzen und vier weißen Taten (Nr. 135–142)",
    count: 8,
    alwaysActive: false,
  },
  {
    id: "lay-vows",
    label: "5 Laiengelübde (Panca Sila)",
    labelShort: "Laien",
    description: "Die fünf lebenslangen Gelübde für buddhistische Laien (Nr. 143–147)",
    count: 5,
    alwaysActive: false,
  },
];

// ---------------------------------------------------------------------------
// I. Die Zufluchtsversprechen (1–12)
// ---------------------------------------------------------------------------
const REFUGE_COMMITMENTS: Vow[] = [
  {
    id: 1,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Wenn du Zuflucht in Buddha genommen hast, nimm keine Zuflucht mehr in weltlichen Dingen und Gottheiten",
      description: "Einzig in Buddha, Dharma und Sangha Zuflucht nehmen, nicht in weltlichen Göttern oder Kräften.",
      positiveFormulation: "Ich nehme allein in die Drei Juwelen Zuflucht.",
    },
    en: {
      title: "Not seeking refuge in worldly objects and deities once you have taken refuge in the Buddha",
      description: "",
    },
  },
  {
    id: 2,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Wenn du Zuflucht im Dharma genommen hast, schade keinem Lebewesen mehr",
      description: "Kein Lebewesen absichtlich verletzen oder töten.",
      positiveFormulation: "Ich handle zum Wohle aller fühlenden Wesen.",
    },
    en: {
      title: "Not harming any living being once you have taken refuge in the Dharma",
      description: "",
    },
  },
  {
    id: 3,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Wenn du Zuflucht in die Sangha genommen hast, gehe keine engen Verbindungen mehr mit Menschen ein, die nicht auf den Pfad vertrauen",
      description: "Den Umgang mit Menschen meiden, die einen von der Praxis abbringen.",
      positiveFormulation: "Ich suche den Umgang mit weisen und tugendhaften Menschen.",
    },
    en: {
      title: "Not associating closely with people who do not believe in the Path once you have taken refuge in the Sangha",
      description: "",
    },
  },
  {
    id: 4,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Nachdem du Zuflucht in Buddha genommen hast, betrachte alle Darstellungen des Buddha als wäre es der Buddha selbst",
      description: "Jede Darstellung des Buddha, auch beschädigt oder schlicht, mit Respekt behandeln.",
      positiveFormulation: "Ich ehre jede Buddha-Abbildung als Erinnerung an die Erleuchtung.",
    },
    en: {
      title: "Considering any representation of the Buddha, regardless of the quality of its artistry or material, as though it were the Buddha himself",
      description: "",
    },
  },
  {
    id: 5,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Wenn du Zuflucht im Dharma genommen hast, betrachte jeden Text, selbst einen einzelnen Buchstaben, als Dharma",
      description: "Alle Dharma-Schriften mit Respekt behandeln, nicht auf den Boden legen oder verunehren.",
      positiveFormulation: "Ich behandle alle Dharma-Texte mit Wertschätzung und Sorgfalt.",
    },
    en: {
      title: "Considering any written material at all, from a single letter on up, as though it were the Dharma itself",
      description: "",
    },
  },
  {
    id: 6,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Wenn du Zuflucht in die Sangha genommen hast, behandle selbst das kleinste Stückchen einer Safran-Robe als wäre es die Sangha selbst",
      description: "Respekt gegenüber allen Mitgliedern der Gemeinschaft, selbst wenn sie fehlerhaft erscheinen.",
      positiveFormulation: "Ich respektiere alle Mitglieder der spirituellen Gemeinschaft.",
    },
    en: {
      title: "Considering even a single scrap of the saffron robe as though it were the Sangha itself",
      description: "",
    },
  },
  {
    id: 7,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Nimm immer und immer wieder Zuflucht, indem du dir die guten Eigenschaften der Zufluchtsobjekte vergegenwärtigst",
      description: "Auch in schwierigen Zeiten die Zuflucht nicht aufgeben.",
      positiveFormulation: "Ich vertraue auf die Drei Juwelen in allen Lebenslagen.",
    },
    en: {
      title: "Going for refuge over and over again, by calling to mind the good qualities of the refuge objects",
      description: "",
    },
  },
  {
    id: 8,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Widme, zur Erinnerung an ihre Güte, den ersten Teil jeder Mahlzeit und jedes Getränks den Zufluchtsobjekten",
      description: "Regelmäßig Opfergaben (physisch oder mental) an Buddha, Dharma und Sangha darbringen.",
      positiveFormulation: "Ich bringe täglich Gaben an die Drei Juwelen dar.",
    },
    en: {
      title: "In remembrance of their kindness, offering the first part of any food or drink to the refuge objects",
      description: "",
    },
  },
  {
    id: 9,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Ermutige andere, Zuflucht zu nehmen",
      description: "Durch eigenes Beispiel und sanfte Worte andere zur Zuflucht in die Drei Juwelen inspirieren.",
      positiveFormulation: "Ich inspiriere andere durch mein Vorbild zum Dharma.",
    },
    en: {
      title: "Encouraging others to take refuge",
      description: "",
    },
  },
  {
    id: 10,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Nimm dreimal tagsüber und dreimal nachts Zuflucht, indem du dir die Vorteile der Zufluchtnahme vergegenwärtigst",
      description: "Morgens, mittags und abends die Zufluchtnahme rezitieren oder im Geist erneuern.",
      positiveFormulation: "Ich erneuere dreimal täglich meine Zuflucht zu Buddha, Dharma und Sangha.",
    },
    en: {
      title: "Taking refuge three times each day, and three times each night, by bringing to mind the benefits of doing so",
      description: "",
    },
  },
  {
    id: 11,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Lege bei jeder Aktivität, die du unternimmst, dein ganzes Vertrauen in die Zufluchtsobjekte",
      description: "In allen Lebenssituationen auf die Drei Juwelen vertrauen.",
      positiveFormulation: "Ich handle mit Vertrauen in die Drei Juwelen.",
    },
    en: {
      title: "To put all your trust in the objects of refuge, during any activity you may undertake at all",
      description: "",
    },
  },
  {
    id: 12,
    category: "refuge-commitments",
    alwaysActive: false,
    de: {
      title: "Gib die drei Juwelen nie auf, in keiner Situation, selbst wenn es dich dein Leben kostet, und nicht einmal im Scherz",
      description: "Selbst um den Preis des Lebens die Zuflucht in die Drei Juwelen nicht aufgeben.",
      positiveFormulation: "Ich halte an meiner Zuflucht fest, unter allen Umständen.",
    },
    en: {
      title: "Not giving up the Three Jewels, even if it should cost you your life, and in every situation from that on down to doing so in jest",
      description: "",
    },
  },
];

// ---------------------------------------------------------------------------
// II. Die zehn Tugenden (13–22)
// ---------------------------------------------------------------------------
const TEN_VIRTUES: Vow[] = [
  {
    id: 13,
    category: "ten-virtues",
    titleSanskrit: "Pranatipata-virati",
    alwaysActive: true,
    de: {
      title: "1. Töten",
      description: "Verzicht auf das Töten von Lebewesen. Achtung vor dem Leben aller fühlenden Wesen.",
      conditions: "Gilt als gebrochen, wenn man bewusst und vorsätzlich ein Lebewesen tötet oder töten lässt.",
      positiveFormulation: "Ich schütze und bewahre das Leben aller fühlenden Wesen.",
      karmicCorrelation: "Langes Leben, Gesundheit und Vitalität.",
      reflectionQuestions: [
        "Habe ich heute einem Lebewesen geschadet?",
        "Habe ich Mitgefühl für alle fühlenden Wesen empfunden?",
        "Gab es Situationen, in denen ich Leben hätte schützen können?",
        "Wie kann ich morgen achtsamer mit dem Leben anderer umgehen?",
      ],
      everydayExamples: [
        "Insekten vorsichtig nach draußen bringen statt zu töten",
        "Bewusste Ernährungsentscheidungen treffen",
        "Pflanzen und Tiere im Garten schützen",
      ],
    },
    en: { title: "1. Killing", description: "" },
  },
  {
    id: 14,
    category: "ten-virtues",
    titleSanskrit: "Adattadana-virati",
    alwaysActive: true,
    de: {
      title: "2. Stehlen",
      description: "Verzicht auf das Nehmen von nicht Gegebenem. Respekt vor dem Eigentum anderer.",
      conditions: "Gilt als gebrochen, wenn man sich bewusst etwas aneignet, das einem nicht gehört.",
      positiveFormulation: "Ich bin großzügig und respektiere das Eigentum anderer.",
      karmicCorrelation: "Wohlstand und materielle Sicherheit.",
      reflectionQuestions: [
        "Habe ich heute etwas genommen, das mir nicht gehört?",
        "War ich ehrlich in allen finanziellen Angelegenheiten?",
        "Habe ich die Großzügigkeit anderer gewürdigt?",
        "Wie kann ich morgen großzügiger sein?",
      ],
      everydayExamples: [
        "Ehrlich bei der Steuererklärung sein",
        "Geliehene Gegenstände pünktlich zurückgeben",
        "Faire Preise bezahlen",
      ],
    },
    en: { title: "2. Stealing", description: "" },
  },
  {
    id: 15,
    category: "ten-virtues",
    titleSanskrit: "Kamamithyacara-virati",
    alwaysActive: true,
    de: {
      title: "3. Sexuelles Fehlverhalten",
      description: "Verzicht auf sexuelles Fehlverhalten. Respektvolles und verantwortungsvolles sexuelles Handeln.",
      conditions: "Gilt als gebrochen bei Ehebruch, Missbrauch oder Verletzung von Vertrauensverhältnissen.",
      positiveFormulation: "Ich handle in Beziehungen respektvoll und verantwortungsbewusst.",
      karmicCorrelation: "Harmonische Beziehungen und Vertrauenswürdigkeit.",
      reflectionQuestions: [
        "Habe ich heute in meinen Beziehungen respektvoll gehandelt?",
        "War ich loyal und ehrlich gegenüber meinem Partner?",
        "Habe ich die Grenzen anderer respektiert?",
        "Wie kann ich morgen achtsamer in Beziehungen sein?",
      ],
      everydayExamples: [
        "Treue und Ehrlichkeit in der Partnerschaft",
        "Respektieren der Grenzen anderer",
        "Verantwortungsvoller Umgang mit Intimität",
      ],
    },
    en: { title: "3. exual misconduct", description: "" },
  },
  {
    id: 16,
    category: "ten-virtues",
    titleSanskrit: "Mrsavada-virati",
    alwaysActive: true,
    de: {
      title: "4. Lügen",
      description: "Verzicht auf unwahre Rede. Ehrlichkeit und Aufrichtigkeit in allen Aussagen.",
      conditions: "Gilt als gebrochen, wenn man bewusst die Unwahrheit sagt, um andere zu täuschen.",
      positiveFormulation: "Ich spreche stets die Wahrheit und bin aufrichtig.",
      karmicCorrelation: "Vertrauenswürdigkeit und ein guter Ruf.",
      reflectionQuestions: [
        "Habe ich heute die Wahrheit gesagt?",
        "Gab es Situationen, in denen ich beschönigt habe?",
        "War ich ehrlich zu mir selbst?",
        "Wie kann ich morgen aufrichtiger kommunizieren?",
      ],
      everydayExamples: [
        "Unangenehme Wahrheiten respektvoll aussprechen",
        "Keine Notlügen zur Vermeidung von Konflikten",
        "Ehrlich über eigene Fehler sprechen",
      ],
    },
    en: { title: "4. Lying", description: "" },
  },
  {
    id: 17,
    category: "ten-virtues",
    titleSanskrit: "Paisunyavada-virati",
    alwaysActive: true,
    de: {
      title: "5. Entzweiende Rede",
      description: "Verzicht auf Klatsch und Verleumdung, die Beziehungen zerreißt.",
      conditions: "Gilt als gebrochen, wenn man bewusst Gerüchte verbreitet, die Beziehungen schädigen.",
      positiveFormulation: "Ich fördere Harmonie durch meine Worte.",
      karmicCorrelation: "Stabile und harmonische soziale Beziehungen.",
      reflectionQuestions: [
        "Habe ich heute über jemanden hinter seinem Rücken geredet?",
        "Habe ich Gerüchte weitergetragen?",
        "Hätte ich in einem Konflikt vermitteln können?",
        "Wie kann ich morgen versöhnlicher sprechen?",
      ],
      everydayExamples: [
        "Keine Gerüchte im Büro weitergeben",
        "Bei Konflikten zwischen Freunden neutral bleiben",
        "Lobend über andere sprechen",
      ],
    },
    en: { title: "5. Divisive talk", description: "" },
  },
  {
    id: 18,
    category: "ten-virtues",
    titleSanskrit: "Parusyavada-virati",
    alwaysActive: true,
    de: {
      title: "6. Barsche Worte",
      description: "Verzicht auf harte, verletzende oder beleidigende Sprache.",
      conditions: "Gilt als gebrochen, wenn man absichtlich verletzend spricht.",
      positiveFormulation: "Ich spreche freundlich und respektvoll.",
      karmicCorrelation: "Wohlklang der Stimme, angenehme Begegnungen.",
      reflectionQuestions: [
        "Habe ich heute jemanden mit Worten verletzt?",
        "War mein Ton heute respektvoll?",
        "Hätte ich in einer Situation geduldiger sein können?",
        "Wie kann ich morgen sanftmütiger sprechen?",
      ],
      everydayExamples: [
        "Auch in Konflikten ruhig und sachlich bleiben",
        "Kritik konstruktiv und freundlich formulieren",
        "Keine sarkastischen oder abwertenden Kommentare",
      ],
    },
    en: { title: "6. Harsh words", description: "" },
  },
  {
    id: 19,
    category: "ten-virtues",
    titleSanskrit: "Sambhinnapralapa-virati",
    alwaysActive: true,
    de: {
      title: "7. Bedeutungsloses Gerede",
      description: "Verzicht auf sinnloses, ablenkungsreiches Geschwätz ohne Nutzen.",
      conditions: "Gilt als gebrochen bei übermäßigem zwecklosem Gerede, das Zeit vergeudet.",
      positiveFormulation: "Ich spreche mit Bedacht und Sinn.",
      karmicCorrelation: "Wirkungsvolle, respektierte Sprache.",
      reflectionQuestions: [
        "Habe ich heute sinnvoll gesprochen?",
        "Habe ich Zeit mit Klatsch vergeudet?",
        "Waren meine Worte heute hilfreich?",
        "Wie kann ich morgen bewusster kommunizieren?",
      ],
      everydayExamples: [
        "Meetings kurz und zielorientiert halten",
        "Bewusstes Schweigen als Praxis",
        "Gespräche auf Wesentliches fokussieren",
      ],
    },
    en: { title: "7. Useless talk", description: "" },
  },
  {
    id: 20,
    category: "ten-virtues",
    titleSanskrit: "Abhidhya-virati",
    alwaysActive: true,
    de: {
      title: "8. Begehren / Neid",
      description: "Verzicht auf Neid und habgieriges Begehren nach dem, was anderen gehört.",
      conditions: "Gilt als gebrochen, wenn man intensiv und bewusst nach dem Besitz anderer begehrt.",
      positiveFormulation: "Ich kultiviere Zufriedenheit und freue mich am Glück anderer.",
      karmicCorrelation: "Innere Zufriedenheit und Freiheit von Gier.",
      reflectionQuestions: [
        "Habe ich heute Neid auf jemanden empfunden?",
        "War ich mit dem zufrieden, was ich habe?",
        "Konnte ich mich über den Erfolg anderer freuen?",
        "Wie kann ich morgen dankbarer sein?",
      ],
      everydayExamples: [
        "Dankbarkeit für das Vorhandene üben",
        "Sich aufrichtig über Erfolge anderer freuen",
        "Konsumimpulse bewusst beobachten",
      ],
    },
    en: { title: "8. Craving", description: "" },
  },
  {
    id: 21,
    category: "ten-virtues",
    titleSanskrit: "Vyapada-virati",
    alwaysActive: true,
    de: {
      title: "9. Schadenfreude",
      description: "Verzicht auf Schadenfreude und den Wunsch, anderen Schaden zu wünschen.",
      conditions: "Gilt als gebrochen, wenn man anderen aktiv Schaden wünscht oder sich daran erfreut.",
      positiveFormulation: "Ich wünsche allen Wesen Freude und Wohlergehen.",
      karmicCorrelation: "Innerer Frieden und liebevolle Beziehungen.",
      reflectionQuestions: [
        "Habe ich heute jemandem Böses gewünscht?",
        "Empfand ich Schadenfreude bei Misserfolgen anderer?",
        "War mein Herz heute offen für alle Menschen?",
        "Wie kann ich morgen mitfühlender sein?",
      ],
      everydayExamples: [
        "Für schwierige Menschen liebende Güte üben",
        "Keine abwertenden Gedanken bei Misserfolgen anderer",
        "Schwierige Menschen als Lehrende sehen",
      ],
    },
    en: { title: "9. Ill-will", description: "" },
  },
  {
    id: 22,
    category: "ten-virtues",
    titleSanskrit: "Mithyadrsti-virati",
    alwaysActive: true,
    de: {
      title: "10. Falsche Weltsicht",
      description: "Verzicht auf nihilistische Ansichten, die Karma, Wiedergeburt oder ethisches Handeln leugnen.",
      conditions: "Gilt als gebrochen, wenn man ernsthaft und anhaltend eine falsche Weltsicht annimmt.",
      positiveFormulation: "Ich kultiviere rechte Ansicht über Ursache und Wirkung.",
      karmicCorrelation: "Weisheit und spirituelle Klarheit.",
      reflectionQuestions: [
        "Habe ich heute zu Recht über das Karma nachgedacht?",
        "War meine Weltsicht heute von Weisheit geleitet?",
        "Habe ich nihilistische Gedanken bemerkt?",
        "Wie kann ich morgen die rechte Ansicht stärken?",
      ],
      everydayExamples: [
        "Über Kausalität und Karma nachdenken",
        "Eigene Handlungen auf ihre ethischen Konsequenzen prüfen",
        "Buddhistische Philosophie studieren",
      ],
    },
    en: { title: "10. Wrong views", description: "" },
  },
];

// ---------------------------------------------------------------------------
// III.A Grundsatz-Bodhisattva-Gelübde (23–57)
// ---------------------------------------------------------------------------
const BODHISATTVA_ROOT: Vow[] = [
  {
    id: 23,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Dich selbst loben, aus dem Verlangen nach Gewinn oder Anerkennung",
      description: "Aus Anhaftung an Gewinn und Ehre sich selbst loben und andere herabsetzen.",
      positiveFormulation: "Ich würdige die Qualitäten anderer und bleibe bescheiden.",
    },
    en: { title: "Praising yourself, out of attachment to gain or honor", description: "" },
  },
  {
    id: 24,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Andere kritisieren, aus dem Verlangen nach Gewinn oder Anerkennung",
      description: "Aus Anhaftung an Gewinn und Ehre andere kritisieren und herabsetzen.",
      positiveFormulation: "Ich spreche über andere mit Respekt und Freundlichkeit.",
    },
    en: { title: "Criticizing others, out of attachment to gain or honor", description: "" },
  },
  {
    id: 25,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Aus Besitzgier das Dharma nicht teilen",
      description: "Aus Geiz Dharma-Lehren nicht mit Bedürftigen teilen.",
      positiveFormulation: "Ich teile großzügig Dharma-Unterweisungen.",
    },
    en: { title: "Failing to give Dharma, due to feelings of possessiveness", description: "" },
  },
  {
    id: 26,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Aus Besitzgier materielle Unterstützung verweigern",
      description: "Aus Geiz materielle Güter mit Bedürftigen nicht teilen.",
      positiveFormulation: "Ich teile großzügig materielle Güter.",
    },
    en: { title: "Failing to give material assistance", description: "" },
  },
  {
    id: 27,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Eine Entschuldigung nicht annehmen",
      description: "Anderen nicht vergeben, selbst wenn sie sich entschuldigen, und im Zorn verharren.",
      positiveFormulation: "Ich vergebe bereitwillig und lasse Zorn los.",
    },
    en: { title: "Failing to accept someone's apology", description: "" },
  },
  {
    id: 28,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Jemanden schlagen",
      description: "Einen anderen Menschen körperlich schlagen oder angreifen.",
      positiveFormulation: "Ich handle gewaltlos und mit Mitgefühl.",
    },
    en: { title: "Striking another", description: "" },
  },
  {
    id: 29,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Ablehnung der Mahayana-Lehren",
      description: "Die Lehren des Mahayana aufgeben oder behaupten, sie seien keine Worte des Buddha.",
      positiveFormulation: "Ich vertraue auf den Mahayana-Dharma und studiere ihn achtsam.",
    },
    en: { title: "Giving up the greater way", description: "" },
  },
  {
    id: 30,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Falsches Dharma lehren",
      description: "Dharma-Unterweisungen vermitteln, die nicht dem authentischen Dharma entsprechen.",
      positiveFormulation: "Ich lehre nur authentischen, hilfreichen Dharma.",
    },
    en: { title: "Teaching false dharma", description: "" },
  },
  {
    id: 31,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Stehlen, was dem Buddha-Juwel gehört",
      description: "Eigentum des Buddha entwenden oder missbrauchen.",
      positiveFormulation: "Ich schütze und achte das Eigentum der Drei Juwelen.",
    },
    en: { title: "Stealing what belongs to the Buddha Jewel", description: "" },
  },
  {
    id: 32,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Stehlen, was dem Dharma-Juwel gehört",
      description: "Eigentum des Dharma entwenden oder missbrauchen.",
      positiveFormulation: "Ich achte das Eigentum des Dharma.",
    },
    en: { title: "Stealing what belongs to the Dharma Jewel", description: "" },
  },
  {
    id: 33,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Stehlen, was dem Sangha-Juwel gehört",
      description: "Eigentum der Sangha entwenden oder missbrauchen.",
      positiveFormulation: "Ich achte das Eigentum der Sangha.",
    },
    en: { title: "Stealing what belongs to the Sangha Jewel", description: "" },
  },
  {
    id: 34,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Das höchste Dharma aufgeben, indem du die gesamten Schriften der Zuhörer abwertest",
      description: "Die Schriften des Shravakayana als minderwertig herabsetzen.",
      positiveFormulation: "Ich achte alle buddhistischen Fahrzeuge gleichermaßen.",
    },
    en: { title: "Giving up the highest Dharma by discounting the scriptural collections of the way of listeners", description: "" },
  },
  {
    id: 35,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Das höchste Dharma aufgeben, indem du die gesamten Schriften der alleinverwirklichten Buddhas abwertest",
      description: "Die Schriften des Pratyekabuddhayana herabsetzen.",
      positiveFormulation: "Ich achte den Weg der alleinverwirklichten Buddhas.",
    },
    en: { title: "Giving up the highest Dharma by discounting the scriptural collections of the way of self-made buddhas", description: "" },
  },
  {
    id: 36,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Das höchste Dharma aufgeben, indem du die gesamten Schriften der Mahayana Unterweisungen abwertest",
      description: "Die Mahayana-Schriften als nutzlos oder falsch bezeichnen.",
      positiveFormulation: "Ich schätze und studiere die Mahayana-Schriften.",
    },
    en: { title: "Giving up the highest Dharma by discounting the scriptural collections of the greater way", description: "" },
  },
  {
    id: 37,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Eine ordinierte Person schlagen oder einsperren oder der goldenen Robe berauben",
      description: "Ordinierte aus dem Dharma vertreiben, ihre Roben wegnehmen oder sie misshandeln.",
      positiveFormulation: "Ich unterstütze und schütze alle Ordinierten.",
    },
    en: { title: "Taking away the golden robes, beating, or incarcerating an ordained person", description: "" },
  },
  {
    id: 38,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Jemand zur Aufgabe der Ordination bringen",
      description: "Jemanden dazu bringen, seine Ordination aufzugeben.",
      positiveFormulation: "Ich ermutige Ordinierte in ihrer Praxis.",
    },
    en: { title: "Removing someone from the status of an ordained person", description: "" },
  },
  {
    id: 39,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die unmittelbare Missetat des Vatermordes",
      description: "Vater oder Mutter töten, einen Arhat töten, den Buddha verletzen oder die Sangha spalten.",
      positiveFormulation: "Ich vermeide alle schwerwiegenden negativen Handlungen.",
    },
    en: { title: "Committing the immediate misdeed of killing your father", description: "" },
  },
  {
    id: 40,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die unmittelbare Missetat des Muttermordes",
      description: "Die schwerwiegende unmittelbare Missetat des Muttermordes begehen.",
      positiveFormulation: "Ich vermeide alle schwerwiegenden negativen Handlungen.",
    },
    en: { title: "Committing the immediate misdeed of killing your mother", description: "" },
  },
  {
    id: 41,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die unmittelbare Missetat, einen Arhat zu töten",
      description: "Die unmittelbare Missetat, einen vollständig Erleuchteten zu töten.",
      positiveFormulation: "Ich vermeide alle schwerwiegenden negativen Handlungen.",
    },
    en: { title: "Committing the immediate misdeed of killing an enemy destroyer", description: "" },
  },
  {
    id: 42,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die unmittelbare Missetat, eine Sangha-Gemeinschaft zu spalten",
      description: "Durch Fehlinformation oder Manipulation eine buddhistische Gemeinschaft zu spalten.",
      positiveFormulation: "Ich fördere Harmonie und Einheit in der Sangha.",
    },
    en: { title: "Committing the immediate misdeed of creating schism in the Sangha", description: "" },
  },
  {
    id: 43,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die unmittelbare Missetat, in böser Absicht einem Buddha eine blutende Wunde zuzufügen",
      description: "In bösartiger Absicht einem Buddha körperlichen Schaden zuzufügen.",
      positiveFormulation: "Ich vermeide alle schwerwiegenden negativen Handlungen.",
    },
    en: { title: "Committing the immediate misdeed of drawing blood from a Buddha with evil intent", description: "" },
  },
  {
    id: 44,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "An falscher Weltsicht festhalten",
      description: "Nihilistische oder extremistische Ansichten annehmen, die das Karma-Gesetz leugnen.",
      positiveFormulation: "Ich pflege die rechte Ansicht über Ursache und Wirkung.",
    },
    en: { title: "Holding wrong views", description: "" },
  },
  {
    id: 45,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Dörfer zerstören",
      description: "Dörfer oder kleine Siedlungen zerstören.",
      positiveFormulation: "Ich schütze die Lebensräume aller Wesen.",
    },
    en: { title: "Destroying towns", description: "" },
  },
  {
    id: 46,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Städte zerstören",
      description: "Städte zerstören, in denen viele Wesen leben.",
      positiveFormulation: "Ich schütze die Lebensräume aller Wesen.",
    },
    en: { title: "Destroying cities", description: "" },
  },
  {
    id: 47,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Ganze Landstriche zerstören",
      description: "Ganze Gebiete durch Krieg, Gift oder andere Mittel zerstören.",
      positiveFormulation: "Ich schütze alle Lebensräume.",
    },
    en: { title: "Destroying whole areas", description: "" },
  },
  {
    id: 48,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Ganze Länder zerstören",
      description: "Ganze Länder zerstören, in denen viele Wesen leben.",
      positiveFormulation: "Ich setze mich für den Schutz aller Lebewesen ein.",
    },
    en: { title: "Destroying entire countries", description: "" },
  },
  {
    id: 49,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Einer Person, die geistig noch nicht bereit ist, Leerheit lehren",
      description: "Die Lehre von der Leerheit an Menschen vermitteln, die sie missverstehen könnten.",
      positiveFormulation: "Ich lehre den Dharma angemessen nach den Fähigkeiten der Hörer.",
    },
    en: { title: "Teaching emptiness to a person who is not yet mentally prepared", description: "" },
  },
  {
    id: 50,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Eine Person vom Streben nach totaler Erleuchtung abbringen",
      description: "Andere davon abhalten, den vollständigen Pfad zur Erleuchtung zu verfolgen.",
      positiveFormulation: "Ich ermutige alle Wesen auf ihrem Weg zur Erleuchtung.",
    },
    en: { title: "Causing a person to turn back from total enlightenment", description: "" },
  },
  {
    id: 51,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Eine Person dazu bringen, ihre Freiheits-Gelübde aufzugeben",
      description: "Andere dazu bringen, ihre individuellen Befreiungsgelübde aufzugeben.",
      positiveFormulation: "Ich ermutige alle, ihre Gelübde zu halten.",
    },
    en: { title: "Causing a person to give up the morality of freedom", description: "" },
  },
  {
    id: 52,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Behaupten, dass man auf dem Weg der Zuhörer Begierde und den Rest nicht eliminieren kann",
      description: "Das Shravakayana als unfähig zur vollständigen Befreiung darstellen.",
      positiveFormulation: "Ich achte alle buddhistischen Fahrzeuge gleichermaßen.",
    },
    en: { title: "Holding that a person cannot eliminate desire and the rest by following the way of the learner", description: "" },
  },
  {
    id: 53,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Jemanden kritisieren, um anderen zu gefallen",
      description: "Aus dem Wunsch, bei anderen gut dazustehen, jemanden kritisieren.",
      positiveFormulation: "Ich spreche über andere nur aus echtem Mitgefühl.",
    },
    en: { title: "Criticizing someone else due to one's desire for the adulation of others", description: "" },
  },
  {
    id: 54,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Behaupten, dass du Leerheit direkt erfahren hast, obwohl es nicht stimmt",
      description: "Fälschlich behaupten, die tiefe Leerheit direkt verwirklicht zu haben.",
      positiveFormulation: "Ich bin ehrlich über meine spirituellen Verwirklichungen.",
    },
    en: { title: "Professing the complete opposite by saying you have seen emptiness directly when you have not", description: "" },
  },
  {
    id: 55,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Annehmen, was den Juwelen, der Sangha oder einem einzelnen Ordinierten gehört, wenn es dir jemand anbietet",
      description: "Güter annehmen, die den Drei Juwelen gehören.",
      positiveFormulation: "Ich lehne unrechtmäßig erworbene Güter ab.",
    },
    en: { title: "Accepting what belongs to the Jewels, to the Sangha, or to an individual monk when someone presents it to you", description: "" },
  },
  {
    id: 56,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Die Praxis der Stille ablehnen und aus diesem Grund die Besitztümer der Meditierer an diejenigen weitergeben, deren Praxis Rezitation ist",
      description: "Als Einflussreicher unfaire oder schädliche Regeln aufstellen.",
      positiveFormulation: "Ich setze mich für gerechte und heilsame Regeln ein.",
    },
    en: { title: "Rejecting the practice of quietude, and giving the possessions of meditators to those whose practice is recitation", description: "" },
  },
  {
    id: 57,
    category: "bodhisattva-root",
    alwaysActive: false,
    de: {
      title: "Den Wunsch nach vollständiger Erleuchtung aufgeben",
      description: "Den Erleuchtungsgeist (Bodhicitta) — den Wunsch, zum Wohle aller Wesen Erleuchtung zu erlangen — aufgeben.",
      positiveFormulation: "Ich halte unerschütterlich am Bodhicitta fest.",
    },
    en: { title: "Discarding the Wish for enlightenment", description: "" },
  },
];

// ---------------------------------------------------------------------------
// III.B Zweitrangige Gelübde (58–106)
// ---------------------------------------------------------------------------
const BODHISATTVA_SECONDARY: Vow[] = [
  { id: 58, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Versäumen, den drei Juwelen durch die drei Türen des Ausdrucks Geschenke darzubringen", description: "Es versäumen, täglich den Drei Juwelen Gaben darzubringen.", positiveFormulation: "Ich bringe täglich Opfergaben dar." }, en: { title: "Failing to make offering to the Jewels through the three doors of expression", description: "" } },
  { id: 59, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Fortwährend Gedanken der Begierde zulassen", description: "Den Sinnesgenüssen nachjagen und sich von Begierden leiten lassen.", positiveFormulation: "Ich übe Zufriedenheit und Genügsamkeit." }, en: { title: "Allowing thoughts of desire to go on", description: "" } },
  { id: 60, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Denen keinen Respekt erweisen, die die Bodhisattva Gelübde vor dir genommen haben", description: "Erfahrenere Praktizierende nicht mit gebührendem Respekt behandeln.", positiveFormulation: "Ich zeige Respekt gegenüber erfahrenen Praktizierenden." }, en: { title: "Failing to pay respect to those who have taken the precepts of the bodhisattva before you", description: "" } },
  { id: 61, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Wut oder Faulheit Fragen nicht beantworten", description: "Aufrichtige Fragen anderer aus Wut oder Faulheit unbeantwortet lassen.", positiveFormulation: "Ich beantworte Fragen geduldig und hilfsbereit." }, en: { title: "Failing to answer questions, out of anger or laziness", description: "" } },
  { id: 62, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Stolz, Wut, Faulheit oder aus dem Wunsch, jemanden zu verletzen, eine Einladung nicht annehmen", description: "Wohlgemeinte Einladungen aus Stolz oder Faulheit ablehnen.", positiveFormulation: "Ich nehme Einladungen mit Dankbarkeit an." }, en: { title: "Failing to accept an invitation, out of pride, or a wish to hurt them, anger, or laziness", description: "" } },
  { id: 63, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Wut, Faulheit oder aus dem Wunsch, jemanden zu verletzen, Gold, Silber oder andere materielle Geschenke ablehnen, die ein Gönner dir anbietet", description: "Aufrichtige Geschenke aus Stolz ablehnen.", positiveFormulation: "Ich nehme Geschenke dankbar an." }, en: { title: "Failing to accept gold or silver or any other kind of material wealth that a sponsor has tried to offer to you", description: "" } },
  { id: 64, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Wut, Neid, Faulheit oder aus dem Wunsch, jemanden zu verletzen, Dharma an jene, die es möchten, nicht weitergeben", description: "Es versäumen, den Dharma zu lehren, wenn jemand aufrichtig darum bittet.", positiveFormulation: "Ich teile den Dharma großzügig mit Suchenden." }, en: { title: "Failing to give the Dharma to those who wish it, out of a desire to hurt them, or out of anger or envy or laziness", description: "" } },
  { id: 65, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Wut, Faulheit oder aus dem Wunsch, jemanden zu verletzen, Personen mit zweifelhafter Moral zurückweisen", description: "Wesen, die Fehler begehen, aus Verärgerung aufgeben.", positiveFormulation: "Ich gebe kein Wesen auf, auch wenn es Fehler begeht." }, en: { title: "Rejecting persons with sullied morality, out of feelings of wanting to hurt them, or out of anger or laziness", description: "" } },
  { id: 66, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Die Regeln der Disziplin, die bei anderen Menschen Vertrauen erwecken, selbst nicht einhalten", description: "Die Verhaltensregeln der Disziplin nicht beachten.", positiveFormulation: "Ich folge achtsam den Regeln der Disziplin." }, en: { title: "Failing to follow the rules of the teachings on discipline, which leads other persons to develop faith", description: "" } },
  { id: 67, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Dinge tun, die nur geringen Nutzen für alle Lebewesen haben", description: "Möglichkeiten, anderen zu helfen, nicht wahrnehmen.", positiveFormulation: "Ich nutze jede Gelegenheit, anderen zu helfen." }, en: { title: "Performing deeds which are only of lesser benefit for all living beings", description: "" } },
  { id: 68, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Mitgefühl eine Regel nicht brechen", description: "Besondere Qualifikationen nötig — muss mit einem qualifizierten Lama studiert werden, bevor man es anwendet.", positiveFormulation: "Ich handle mit Mitgefühl und Weisheit." }, en: { title: "Failing to break a rule out of compassion", description: "" } },
  { id: 69, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich seinen Lebensunterhalt unredlich verdienen: durch Vortäuschung", description: "Den eigenen Lebensunterhalt durch Vortäuschung unrechtmäßig verdienen.", positiveFormulation: "Ich verdiene meinen Lebensunterhalt ehrlich." }, en: { title: "Taking up wrong livelihood of trying to obtain things through pretending", description: "" } },
  { id: 70, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich seinen Lebensunterhalt unredlich verdienen: durch Schmeicheln", description: "Den eigenen Lebensunterhalt durch Schmeichelei unrechtmäßig verdienen.", positiveFormulation: "Ich bin aufrichtig in meinen Worten." }, en: { title: "Taking up wrong livelihood of trying to obtain things through flattering", description: "" } },
  { id: 71, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich seinen Lebensunterhalt unredlich verdienen: durch Andeutungen", description: "Den eigenen Lebensunterhalt durch Andeutungen und indirekten Druck unrechtmäßig verdienen.", positiveFormulation: "Ich kommuniziere direkt und ehrlich." }, en: { title: "Taking up wrong livelihood of trying to obtain things through hinting", description: "" } },
  { id: 72, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich seinen Lebensunterhalt unredlich verdienen: durch Nötigung", description: "Den eigenen Lebensunterhalt durch Zwang oder Nötigung unrechtmäßig verdienen.", positiveFormulation: "Ich handle ohne Zwang und Druck auf andere." }, en: { title: "Taking up wrong livelihood of trying to obtain things through forcing", description: "" } },
  { id: 73, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich seinen Lebensunterhalt unredlich verdienen: durch Ködern", description: "Den eigenen Lebensunterhalt durch Ködern und Manipulation unrechtmäßig verdienen.", positiveFormulation: "Ich verdiene meinen Lebensunterhalt ohne Manipulation." }, en: { title: "Taking up wrong livelihood of trying to obtain things through baiting", description: "" } },
  { id: 74, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Selbst ungezügelt und leichtfertig sein oder andere dazu verführen", description: "Sich der Trägheit und Faulheit hingeben oder andere dazu verführen.", positiveFormulation: "Ich übe mich in Fleiß und freudiger Anstrengung." }, en: { title: "Forgetting yourself and acting wild, or getting others to act wild, and so on", description: "" } },
  { id: 75, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Anderen sagen, sie müssen ihr eigenes Erreichen von Nirvana verzögern, um anderen helfen zu können", description: "Anderen sagen, sie sollen nicht nach Nirvana streben, um andere zu unterstützen.", positiveFormulation: "Ich ermutige alle Wesen auf ihrem Weg zur Erleuchtung." }, en: { title: "Thinking that you only have to travel through the circle of suffering life", description: "" } },
  { id: 76, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Gerüchte über dich nicht aus der Welt schaffen", description: "Handlungen nicht meiden, die dem Ruf des Dharma schaden.", positiveFormulation: "Ich handle so, dass der Dharma in gutem Licht erscheint." }, en: { title: "Failing to put a stop to rumors about yourself", description: "" } },
  { id: 77, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Jemanden nicht korrigieren, auch wenn du ihn mit Nachdruck zurechtweisen musst", description: "Eigene Verblendungen nicht aktiv bearbeiten und korrigieren.", positiveFormulation: "Ich arbeite aktiv an meinen Verblendungen." }, en: { title: "Failing to correct someone when it must be done in a negative way", description: "" } },
  { id: 78, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Die vier Punkte der Tugendpraxis verlieren, indem du auf Schreien mit Schreien reagierst", description: "Beleidigungen oder Schreien mit gleichem vergelten.", positiveFormulation: "Ich reagiere auf Schreien mit Ruhe und Geduld." }, en: { title: "Losing the four points of the practice of virtue by responding to scolding with scolding", description: "" } },
  { id: 79, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Die vier Punkte der Tugendpraxis verlieren, indem du auf Wut mit Wut reagierst", description: "Auf Wut mit Wut reagieren.", positiveFormulation: "Ich reagiere auf Wut mit Verständnis und Geduld." }, en: { title: "Losing the four points of the practice of virtue by responding to anger with anger", description: "" } },
  { id: 80, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Die vier Punkte der Tugendpraxis verlieren, indem du auf Schlagen mit Schlagen reagierst", description: "Auf körperliche Gewalt mit Gegenwalt reagieren.", positiveFormulation: "Ich reagiere auf Gewalt mit Gewaltlosigkeit." }, en: { title: "Losing the four points of the practice of virtue by responding to being beaten by beating", description: "" } },
  { id: 81, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Die vier Punkte der Tugendpraxis verlieren, indem du auf Kritik mit Kritik reagierst", description: "Auf Kritik mit Gegenkritik reagieren.", positiveFormulation: "Ich nehme Kritik mit Offenheit auf." }, en: { title: "Losing the four points of the practice of virtue by responding to being criticized by criticizing", description: "" } },
  { id: 82, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Stolz, Faulheit oder dem Wunsch, jemanden zu verletzen, dich nicht angemessen erklären gegenüber jemandem, der wütend auf dich ist; ihn einfach ignorieren", description: "Jemanden ignorieren, der wütend auf einen ist, ohne sich zu erklären.", positiveFormulation: "Ich erkläre mich ruhig gegenüber wütenden Menschen." }, en: { title: "Simply ignoring those who are angry at you, by failing to explain yourself to them in an appropriate manner", description: "" } },
  { id: 83, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich weigern, einer anderen Person zu vergeben, wenn sich diese dafür entschuldigt, dass sie dir Unrecht getan hat", description: "Aufrichtige Entschuldigungen aus Stolz oder Zorn ablehnen.", positiveFormulation: "Ich nehme aufrichtige Entschuldigungen gnädig an." }, en: { title: "Refusing to forgive another person when they apologize for doing something wrong to you", description: "" } },
  { id: 84, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Fortwährend Gedanken der Wut zulassen", description: "Ärgerliche Gedanken nähren statt sie aufzulösen.", positiveFormulation: "Ich löse Ärger auf, sobald er entsteht." }, en: { title: "Allowing thoughts of anger to go on", description: "" } },
  { id: 85, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Schüler um dich sammeln mit dem Wunsch, Nutzen oder Verehrung von ihnen zu bekommen", description: "Aus Ehrgeiz nach Anhängern und Einfluss streben.", positiveFormulation: "Ich diene anderen ohne Streben nach Ruhm." }, en: { title: "Collecting a group of disciples out of a desire to have others pay honor to you, serve you, or make material offerings to you", description: "" } },
  { id: 86, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Nichts gegen die eigene Faulheit etc. unternehmen", description: "Sich der Trägheit und Faulheit hingeben.", positiveFormulation: "Ich übe mich in Fleiß und achtsamer Anstrengung." }, en: { title: "Failing to dispel your laziness and the like", description: "" } },
  { id: 87, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Weil es dir Freude bereitet, Zeit mit nutzlosem Gerede verschwenden", description: "Sich in Klatsch und sinnlosem Gerede verlieren.", positiveFormulation: "Ich spreche sinnvoll und mit Bedeutung." }, en: { title: "Spending time with busy talk because you enjoy it", description: "" } },
  { id: 88, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich nicht um meditative Konzentration bemühen: aus Faulheit oder um andere zu verletzen", description: "Nicht nach geistiger Ruhe und Konzentration streben.", positiveFormulation: "Ich übe mich regelmäßig in Meditation und Sammlung." }, en: { title: "Failing to seek the meaning of meditative concentration, out of a desire to hurt someone, or laziness", description: "" } },
  { id: 89, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Auftretende Meditationshindernisse nicht bekämpfen: Rastloses Verlangen oder etwas vermissen", description: "Hindernisse in der Meditation nicht aktiv beseitigen.", positiveFormulation: "Ich arbeite aktiv an der Beseitigung von Meditationshindernissen." }, en: { title: "Failing to purge yourself of the meditation obstacle of the pair of restless desire and missing something or someone", description: "" } },
  { id: 90, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Auftretende Meditationshindernisse nicht bekämpfen: Bösartige Gedanken über andere", description: "Bösartige Gedanken in der Meditation nicht auflösen.", positiveFormulation: "Ich kultiviere liebende Güte auch in der Meditation." }, en: { title: "Failing to purge yourself of the meditation obstacle of feelings of malice about someone", description: "" } },
  { id: 91, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Auftretende Meditationshindernisse nicht bekämpfen: Dumpfheit und Schläfrigkeit", description: "Dumpfheit und Schläfrigkeit in der Meditation nicht beseitigen.", positiveFormulation: "Ich übe Wachheit und Klarheit in der Meditation." }, en: { title: "Failing to purge yourself of the meditation obstacle of the pair of drowsiness and mental dullness", description: "" } },
  { id: 92, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Auftretende Meditationshindernisse nicht bekämpfen: Zu Sinnesobjekten hingezogen sein", description: "Anhaftung an Sinnesobjekte in der Meditation nicht überwinden.", positiveFormulation: "Ich löse die Anhaftung an Sinnesobjekte auf." }, en: { title: "Failing to purge yourself of the meditation obstacle of attraction to the objects of the senses", description: "" } },
  { id: 93, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Auftretende Meditationshindernisse nicht bekämpfen: Ungeklärte Zweifel", description: "Zweifel und Unklarheiten in der Praxis nicht auflösen.", positiveFormulation: "Ich kläre Zweifel durch Studium und Kontemplation." }, en: { title: "Failing to purge yourself of the meditation obstacle of unresolved doubts", description: "" } },
  { id: 94, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Das angenehme Gefühl bei der Meditation als wichtige persönliche Errungenschaft betrachten", description: "Die meditative Versenkung als Selbstzweck genießen.", positiveFormulation: "Ich nutze geistige Ruhe als Grundlage für Einsicht." }, en: { title: "Considering the pleasant sensation of meditation to be an important personal attainment", description: "" } },
  { id: 95, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Den Weg der Zuhörer ablehnen", description: "Das Fahrzeug der Hörer (Shravakayana) ablehnen.", positiveFormulation: "Ich achte alle buddhistischen Fahrzeuge gleichermaßen." }, en: { title: "Rejecting the way of the listeners", description: "" } },
  { id: 96, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich in den gesammelten Schriften der Zuhörer befleißigen, wenn du die Gelegenheit hast, dich in den gesammelten Schriften der Bodhisattvas zu üben", description: "Die Vinaya-Praxis über die Bodhisattva-Schriften stellen.", positiveFormulation: "Ich widme mich dem Bodhisattva-Studium mit Vorrang." }, en: { title: "Making efforts in the scriptural collection on the listeners, when you have an opportunity to exert yourself in the scriptural collection of the bodhisattvas", description: "" } },
  { id: 97, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich mit nicht-buddhistischen Texten beschäftigen, für die sich die Mühe nicht lohnt", description: "Weltliche Studien über den Dharma stellen.", positiveFormulation: "Ich widme mich dem Dharma-Studium mit Vorrang." }, en: { title: "Making efforts in non-Buddhist texts where no efforts should be made", description: "" } },
  { id: 98, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Sich hingezogen fühlen zu nicht-buddhistischen Texten, wenn du dich damit beschäftigen musst", description: "Sich für Aktivitäten entscheiden, die dem Dharma nicht dienlich sind.", positiveFormulation: "Ich wähle Aktivitäten, die meine Praxis unterstützen." }, en: { title: "Being attracted to non-Buddhist texts, even in a case where one must make efforts", description: "" } },
  { id: 99, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Mahayana ablehnen, indem du sagst: \"Dieser Teil ist nicht so gut\"", description: "Andere buddhistische Traditionen oder Schulen herabsetzen.", positiveFormulation: "Ich achte alle buddhistischen Traditionen." }, en: { title: "Rejecting the greater way by saying, \"This part is not as good\"", description: "" } },
  { id: 100, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Mahayana ablehnen, indem du sagst: \"Diese Stelle ist nicht gut geschrieben\"", description: "Die Qualität der Bodhisattva-Schriften in Frage stellen.", positiveFormulation: "Ich schätze alle Bodhisattva-Schriften." }, en: { title: "Rejecting the greater way by saying, \"The composition at this point is inferior\"", description: "" } },
  { id: 101, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Mahayana ablehnen, indem du sagst: \"Der Verfasser dieses Teils ist nicht der Beste\"", description: "Den Autor der Bodhisattva-Schriften herabsetzen.", positiveFormulation: "Ich respektiere die Autoren buddhistischer Schriften." }, en: { title: "Rejecting the greater way by saying, \"The author of this part was not the best\"", description: "" } },
  { id: 102, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Mahayana ablehnen, indem du sagst: \"Dieser Teil wird nicht zum Wohle aller Lebewesen beitragen\"", description: "Den Nutzen der Bodhisattva-Schriften in Frage stellen.", positiveFormulation: "Ich vertraue auf den Nutzen aller Dharma-Unterweisungen." }, en: { title: "Rejecting the greater way by saying, \"This part won't contribute to the welfare of living beings\"", description: "" } },
  { id: 103, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Eigenlob aus Stolz oder Wut", description: "Sich selbst statt anderen dienen.", positiveFormulation: "Ich stelle das Wohl anderer über mein eigenes." }, en: { title: "Praising oneself out of pride or anger", description: "" } },
  { id: 104, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Andere kritisieren aus Stolz oder Wut", description: "Für weltlichen Gewinn und Ansehen handeln.", positiveFormulation: "Ich handle zum Wohle aller Wesen, nicht für Gewinn." }, en: { title: "Criticizing others out of pride or anger", description: "" } },
  { id: 105, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Aus Stolz oder Faulheit einen Dharma Vortrag nicht besuchen", description: "Dharma-Unterweisungen aus Stolz oder Faulheit nicht aufsuchen.", positiveFormulation: "Ich suche Dharma-Unterweisungen mit Freude auf." }, en: { title: "Not going to hear the Dharma out of pride or laziness", description: "" } },
  { id: 106, category: "bodhisattva-secondary", alwaysActive: false, de: { title: "Deinen Dharma Lehrer herabsetzen, indem du ihn nicht als Buddha ansiehst, schlecht über ihn sprichst oder mehr an der Aufmachung als am Inhalt interessiert bist", description: "Den Dharma-Lehrer nicht mit entsprechendem Respekt behandeln.", positiveFormulation: "Ich ehre meinen Dharma-Lehrer mit tiefer Wertschätzung." }, en: { title: "Focusing on the vessel and the letters, by focusing on the teacher and not the teachings", description: "" } },
];

// ---------------------------------------------------------------------------
// III.C Zweitrangige Verfehlungen gegen die dritte Moral, anderen Menschen zu helfen (107–125)
// ---------------------------------------------------------------------------
const BODHISATTVA_HELPING: Vow[] = [
  { id: 107, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung bei der Erledigung einer bestimmten Aufgabe braucht", description: "Jemanden, der Hilfe bei einer Aufgabe braucht, im Stich lassen.", positiveFormulation: "Ich helfe anderen bei ihren Aufgaben mit Freude." }, en: { title: "Failing to assist someone in need who is just undertaking a particular task, out of anger or laziness", description: "" } },
  { id: 108, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung beim Reisen braucht", description: "Jemanden, der Hilfe beim Reisen braucht, im Stich lassen.", positiveFormulation: "Ich helfe Reisenden mit Freude." }, en: { title: "Failing to assist someone in need who is trying to get somewhere, out of anger or laziness", description: "" } },
  { id: 109, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung beim Erlernen einer Sprache braucht", description: "Jemanden, der eine Sprache lernen möchte, nicht unterstützen.", positiveFormulation: "Ich unterstütze andere beim Lernen." }, en: { title: "Failing to assist someone in need who is trying to learn a language, out of anger or laziness", description: "" } },
  { id: 110, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung beim Erlernen einer Fertigkeit braucht", description: "Jemanden, der eine Fertigkeit erlernen möchte, nicht unterstützen.", positiveFormulation: "Ich unterstütze andere beim Erlernen von Fertigkeiten." }, en: { title: "Failing to assist someone in need who is trying to learn a skill (as long as it is not something harmful), out of anger or laziness", description: "" } },
  { id: 111, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung beim Schutz ihres Besitzes braucht", description: "Jemanden beim Schutz seines Eigentums nicht unterstützen.", positiveFormulation: "Ich helfe anderen beim Schutz ihres Eigentums." }, en: { title: "Failing to assist someone in need who is trying to protect their belongings, out of anger or laziness", description: "" } },
  { id: 112, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung braucht, um einen Streit zwischen Menschen beizulegen", description: "Bei Konflikten zwischen Menschen nicht vermitteln.", positiveFormulation: "Ich vermittle bei Konflikten mit Mitgefühl." }, en: { title: "Failing to assist someone in need who is trying to fix a split between people, out of anger or laziness", description: "" } },
  { id: 113, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung bei der Planung einer tugendhaften Veranstaltung braucht", description: "Bei der Planung tugendhafter Veranstaltungen nicht helfen.", positiveFormulation: "Ich unterstütze tugendhafte Veranstaltungen." }, en: { title: "Failing to assist someone in need who is planning a virtuous event of some kind, out of anger or laziness", description: "" } },
  { id: 114, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einer Person nicht helfen, die Unterstützung bei einem tugendhaften Projekt braucht", description: "Bei tugendhaften Projekten keine Unterstützung anbieten.", positiveFormulation: "Ich unterstütze tugendhafte Projekte anderer." }, en: { title: "Failing to assist someone in need who is undertaking some more general virtue, out of anger or laziness", description: "" } },
  { id: 115, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit Kranken nicht helfen", description: "Es versäumen, sich um Kranke und Bedürftige zu kümmern.", positiveFormulation: "Ich sorge für Kranke und Hilfsbedürftige." }, en: { title: "Failing to serve the sick, out of anger or laziness", description: "" } },
  { id: 116, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Das geistige oder körperliche Leid eines anderen nicht lindern", description: "Das Leid von Blinden, Tauben, Behinderten, Reisemüden oder unter geistigen Hindernissen Leidenden nicht lindern.", positiveFormulation: "Ich lindere aktiv das Leid anderer." }, en: { title: "Failing to make some attempt to remove the suffering of those who are blind, deaf, handicapped, weary from traveling, or tormented by mental obstacles", description: "" } },
  { id: 117, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Wut oder Faulheit einen anderen nicht mit Argumenten davon abbringen, etwas Gefährliches zu tun", description: "Jemanden, der etwas Gefährliches tut, nicht davon abzubringen.", positiveFormulation: "Ich helfe anderen, gefährliche Handlungen zu vermeiden." }, en: { title: "Failing to give good reasons to those who are acting in a dangerous way, out of anger or laziness", description: "" } },
  { id: 118, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, die Hilfe, die du von jemandem bekommen hast, nicht erwidern", description: "Empfangene Hilfe und Güte nicht erwidern.", positiveFormulation: "Ich zeige Dankbarkeit und erwidere Güte." }, en: { title: "Failing to repay someone who has helped you, by helping them back, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 119, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, die Trauer einer anderen Person nicht lindern", description: "Trauernde und Leidende nicht trösten.", positiveFormulation: "Ich tröste und unterstütze Trauernde und Leidende." }, en: { title: "Failing to dispel another person's grief, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 120, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, jemandem Geld oder andere materielle Dinge verweigern, wenn dieser sie will", description: "Bitten um materielle Hilfe unbegründet ablehnen.", positiveFormulation: "Ich bin großzügig nach meinen Möglichkeiten." }, en: { title: "Failing to give money or other material things to someone who wants them, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 121, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, die Bedürfnisse deiner Schüler nicht erfüllen", description: "Es versäumen, für die Menschen in der eigenen Gemeinschaft zu sorgen.", positiveFormulation: "Ich sorge für meine Schüler und Gemeinschaft." }, en: { title: "Failing to fill the needs of your circle of disciples, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 122, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, mit jemandem nicht gut auskommen", description: "Nicht auf die individuelle Situation und die Bedürfnisse anderer eingehen.", positiveFormulation: "Ich gehe einfühlsam auf die Situation jedes Einzelnen ein." }, en: { title: "Failing to get along with someone, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 123, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder dem Wunsch, den anderen zu verletzen, die guten Eigenschaften eines anderen nicht loben", description: "Die positiven Eigenschaften und Handlungen anderer nicht anerkennen.", positiveFormulation: "Ich erkenne und würdige die guten Eigenschaften anderer." }, en: { title: "Failing to praise someone's good qualities, out of a desire to hurt someone or out of laziness", description: "" } },
  { id: 124, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Aus Faulheit oder unter dem Einfluss von Geistesgiften einen anderen nicht in die Schranken weisen, wenn es notwendig ist", description: "Es versäumen, bei Bedarf angemessene Strenge zu zeigen.", positiveFormulation: "Ich bin, wenn nötig, liebevoll streng zum Wohle anderer." }, en: { title: "Failing to cut someone off when the time has come to do so, out of mental afflictions or laziness", description: "" } },
  { id: 125, category: "bodhisattva-helping", alwaysActive: false, de: { title: "Die eigenen übernatürlichen Kräfte nicht nutzen, um jemandem zu drohen oder ähnliches, wann immer es notwendig ist", description: "Falls man über solche Kräfte verfügt, sie nicht zum Wohle anderer einsetzen.", positiveFormulation: "Ich setze alle verfügbaren Mittel zum Wohle anderer ein." }, en: { title: "Failing to use your supernormal powers to threaten someone or such when needed", description: "" } },
];

// ---------------------------------------------------------------------------
// III.D Zweitrangige Verfehlungen gegen den Wunsch nach Erleuchtung (126–134)
// ---------------------------------------------------------------------------
const BODHISATTVA_ENLIGHTENMENT: Vow[] = [
  { id: 126, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Jene, die es verdienen, nicht mit Dharma unterstützen", description: "Jene, die es verdienen, nicht mit Dharma-Unterweisungen unterstützen.", positiveFormulation: "Ich unterstütze würdige Menschen mit Dharma." }, en: { title: "Failing to support those whom it would be proper to support, with Dharma", description: "" } },
  { id: 127, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Jene, die es verdienen, nicht mit materiellen Dingen unterstützen", description: "Jene, die es verdienen, nicht materiell unterstützen.", positiveFormulation: "Ich unterstütze würdige Menschen materiell." }, en: { title: "Failing to support those whom it would be proper to support, with material things", description: "" } },
  { id: 128, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Deinen Ärger nicht loslassen, wenn dich jemand verletzt hat", description: "Den Ärger über erlittenes Unrecht nicht loslassen.", positiveFormulation: "Ich lasse Ärger los und vergebe." }, en: { title: "Failing to let go of your anger about something someone else had done to hurt you", description: "" } },
  { id: 129, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Unterscheidungen zwischen Menschen treffen: mag ich/mag ich nicht", description: "Zwischen Menschen unterscheiden und Vorlieben entwickeln.", positiveFormulation: "Ich begegne allen Menschen mit gleichmütigem Mitgefühl." }, en: { title: "Discriminating between people, in the sense of liking some and disliking others", description: "" } },
  { id: 130, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Versäumnis, dich einem heiligen Lama anzuvertrauen", description: "Keinen qualifizierten spirituellen Lehrer aufsuchen.", positiveFormulation: "Ich suche die Führung eines qualifizierten Lehrers." }, en: { title: "Failing to take yourself to a holy Lama", description: "" } },
  { id: 131, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Die Praxis des Lernens aufgeben", description: "Das Studium des Dharma aufgeben.", positiveFormulation: "Ich widme mich dem Dharma-Studium mit Beständigkeit." }, en: { title: "Giving up the practice of learning", description: "" } },
  { id: 132, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Das Dharma nicht kontemplieren, nachdem du es studiert hast", description: "Das Studierte nicht in Kontemplation vertiefen.", positiveFormulation: "Ich kontempliere den Dharma nach dem Studium." }, en: { title: "Giving up the practice of contemplating upon what you have learned", description: "" } },
  { id: 133, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Dinge nicht zum Wohle aller anderen benutzen: Essen, Kleidung und alles, was dir sonst zur Verfügung steht", description: "Die eigenen Ressourcen nicht zum Wohle aller anderen benutzen.", positiveFormulation: "Ich nutze meine Ressourcen zum Wohle aller Wesen." }, en: { title: "Failing to have helping others in mind as you partake of food or clothing or any other thing you make use of", description: "" } },
  { id: 134, category: "bodhisattva-enlightenment", alwaysActive: false, de: { title: "Während einer tugendhaften Aktivität nicht daran denken, dass alles Gute, das du tust, dazu dienen soll, Erleuchtung zum Wohle aller Lebewesen zu erlangen", description: "Tugendhafte Handlungen nicht dem Bodhicitta-Streben widmen.", positiveFormulation: "Ich widme alle guten Handlungen dem Wohl aller Wesen." }, en: { title: "Engaging in any virtuous activity without having in mind the wish to reach enlightenment for the sake of all living beings", description: "" } },
];

// ---------------------------------------------------------------------------
// III.E Die schwarzen und die weißen Taten (135–142)
// ---------------------------------------------------------------------------
const BODHISATTVA_BLACK_WHITE: Vow[] = [
  { id: 135, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die schwarze Tat: Bewusst deinen Lama, die Empfänger deiner Darbringungen oder irgendeine andere Person täuschen, indem du sie anlügst", description: "Den eigenen Lehrer oder Förderer bewusst anlügen.", positiveFormulation: "Ich bin stets aufrichtig zu meinem Lehrer und Förderern." }, en: { title: "The black deed of intentionally deceiving your Lama, or those to whom we make offerings, or any such person, by lying to them", description: "" } },
  { id: 136, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die weiße Tat: Niemals, absichtlich oder im Scherz, auch nur ein einziges Lebewesen anlügen", description: "Absolute Wahrhaftigkeit gegenüber jedem Lebewesen.", positiveFormulation: "Ich spreche stets die Wahrheit zu allen Wesen." }, en: { title: "The white deed of never intentionally speaking a lie, even if only in jest, to any single living being there is", description: "" } },
  { id: 137, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die schwarze Tat: Einen anderen dazu bringen, eine gute Tat zu bereuen", description: "Jemanden dazu bringen, eine tugendhafte Handlung zu bereuen.", positiveFormulation: "Ich ermutige andere in ihren tugendhaften Handlungen." }, en: { title: "The black deed of causing another person to regret some virtuous thing they have done", description: "" } },
  { id: 138, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die weiße Tat: Eine Person, die du anleitest, dazu ermutigen, die vollkommene Erleuchtung anzustreben statt sie auf den Pfad des Kleinen Weges zu führen", description: "Menschen auf dem Weg zur vollständigen Erleuchtung führen.", positiveFormulation: "Ich führe andere zur höchsten Erleuchtung." }, en: { title: "The white deed of bringing a person that you are cultivating to strive for total enlightenment, rather than bringing them to the path of the lower way", description: "" } },
  { id: 139, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die schwarze Tat: Aus Zorn etwas Unangenehmes zu einem Bodhisattva sagen", description: "Aus Zorn verletzende Worte zu einem Bodhisattva sagen.", positiveFormulation: "Ich spreche zu Bodhisattvas stets mit Respekt." }, en: { title: "The black deed of saying something unpleasant to a bodhisattva out of anger", description: "" } },
  { id: 140, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die weiße Tat: In jedem Lebewesen den Lehrer, den Buddha selbst sehen und alle Wesen und Dinge als vollständig rein ansehen", description: "In jedem Wesen die Buddha-Natur erkennen.", positiveFormulation: "Ich sehe in jedem Wesen den Buddha selbst." }, en: { title: "The white deed of trying to conceive of every sentient being as the Teacher himself, and to see all beings and things as totally pure", description: "" } },
  { id: 141, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die schwarze Tat: Ein anderes Lebewesen hinterhältig täuschen, ohne persönliche Verantwortung für seine Erleuchtung zu übernehmen", description: "Andere täuschen ohne persönliche Verantwortung für ihre Erleuchtung.", positiveFormulation: "Ich übernehme Verantwortung für das Wohl aller Wesen." }, en: { title: "The black deed of acting in a devious way with any living being, without any sense of personal responsibility for their enlightenment", description: "" } },
  { id: 142, category: "bodhisattva-black-white", alwaysActive: false, de: { title: "Die weiße Tat: Absolute Aufrichtigkeit gegenüber jedem Lebewesen, ohne jegliche Täuschung", description: "Absolute Ehrlichkeit und Integrität gegenüber allen Wesen.", positiveFormulation: "Ich handle mit absoluter Aufrichtigkeit gegenüber allen Wesen." }, en: { title: "The white deed of maintaining an attitude of total honesty towards every living being, free of any kind of deception", description: "" } },
];

// ---------------------------------------------------------------------------
// IV. Die fünf lebenslangen Laiengelübde (143–147)
// ---------------------------------------------------------------------------
const LAY_VOWS: Vow[] = [
  {
    id: 143,
    category: "lay-vows",
    titleSanskrit: "Panatipata veramani",
    alwaysActive: false,
    de: {
      title: "Keinen Menschen oder menschlichen Fötus töten",
      description: "Ich gelobe, mich des Tötens von Menschen zu enthalten.",
      positiveFormulation: "Ich schütze das Leben aller Menschen mit liebender Güte.",
    },
    en: { title: "Not killing a human or a human fetus", description: "" },
  },
  {
    id: 144,
    category: "lay-vows",
    titleSanskrit: "Adinnadana veramani",
    alwaysActive: false,
    de: {
      title: "Nichts von Wert stehlen",
      description: "Ich gelobe, mich des Nehmens von nicht Gegebenem zu enthalten.",
      positiveFormulation: "Ich übe Großzügigkeit und respektiere das Eigentum anderer.",
    },
    en: { title: "Not stealing anything of value", description: "" },
  },
  {
    id: 145,
    category: "lay-vows",
    titleSanskrit: "Kamesu micchacara veramani",
    alwaysActive: false,
    de: {
      title: "Nicht über deine spirituellen Errungenschaften lügen",
      description: "Ich gelobe, nicht über meine spirituellen Verwirklichungen zu lügen.",
      positiveFormulation: "Ich bin ehrlich über meinen spirituellen Stand.",
    },
    en: { title: "Not lying about your spiritual realizations", description: "" },
  },
  {
    id: 146,
    category: "lay-vows",
    titleSanskrit: "Musavada veramani",
    alwaysActive: false,
    de: {
      title: "Keinen Ehebruch begehen",
      description: "Ich gelobe, mich des Ehebruchs zu enthalten.",
      positiveFormulation: "Ich handle verantwortungsvoll und respektvoll in Beziehungen.",
    },
    en: { title: "Not committing adultery", description: "" },
  },
  {
    id: 147,
    category: "lay-vows",
    titleSanskrit: "Surameraya majja pamadatthana veramani",
    alwaysActive: false,
    de: {
      title: "Kein Gebrauch von Alkohol oder Rauschmitteln machen",
      description: "Ich gelobe, mich des Konsums berauschender Mittel zu enthalten, die zu Nachlässigkeit führen.",
      positiveFormulation: "Ich halte meinen Geist klar und achtsam.",
    },
    en: { title: "Not taking alcohol or intoxicants", description: "" },
  },
];

// ---------------------------------------------------------------------------
// Export: Alle Gelübde zusammen
// ---------------------------------------------------------------------------
export const ALL_VOWS: Vow[] = [
  ...REFUGE_COMMITMENTS,
  ...TEN_VIRTUES,
  ...BODHISATTVA_ROOT,
  ...BODHISATTVA_SECONDARY,
  ...BODHISATTVA_HELPING,
  ...BODHISATTVA_ENLIGHTENMENT,
  ...BODHISATTVA_BLACK_WHITE,
  ...LAY_VOWS,
];

/** Schneller Zugriff per ID */
export const VOWS_BY_ID: Record<number, Vow> = Object.fromEntries(
  ALL_VOWS.map((v) => [v.id, v])
);

/** Gelübde nach Kategorie gruppiert */
export function getVowsByCategory(category: VowCategory): Vow[] {
  return ALL_VOWS.filter((v) => v.category === category);
}
